import KEYS from '@/values/keys';
import EdPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import WebSocketMessageRequest from '@/content/models/web-socket-message-request.model';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import { _APP_STORE } from '@/core/values/app.values';
import { SCREEN_MIRRORING_STORE } from '@/screen-mirroring/store/screen-mirroring.store';
import Tools from '@/providers/tools';
import { LocalStorageService } from './local-storage.service';
import { APP_STORAGE_KEYS } from '@/core/enums/app.enum';
import BrowserService from '@/providers/browser-utils/browser.service';
import { COURSE_STORAGE_KEYS, COURSE_TABS } from '@/courses/values/courses.values';
import { SCREEN_MIRRORING_EVENTS } from '@/values/screen.mirroring.values';
import { PRIVATE_ROUTES } from '@/app/private/private.routes';

class ScreenMirroringServiceClass {

  public createListenerBookMode(element: Vue, notifyDeviceRegistration: boolean = true) {
    let bookLabel = element.$t('screenMirroring.bookLabel').toString().toUpperCase();
    document.title = `Eduac - ${bookLabel}`;
    this.createListener(element, 'book', notifyDeviceRegistration);
  }

  public createListenerPaperMode(element: Vue, notifyDeviceRegistration: boolean = true) {
    let paperLabel = element.$t('screenMirroring.paperLabel').toString().toUpperCase();
    document.title = `Eduac - ${paperLabel}`;
    this.createListener(element, 'paper', notifyDeviceRegistration);
  }

  public createListenerNormalMode(element: Vue, notifyDeviceRegistration: boolean = true) {
    document.title = 'Eduac';
    this.createListener(element, 'normal', notifyDeviceRegistration);
  }
 
  public registerDevice(element: Vue) {
    let browserName = BrowserService.getBrowserName();//(e.g. Chrome)
    let browserVersion = BrowserService.getBrowserVersion();//(e.g. 80 in case of Chrome)
    let platformType = BrowserService.getPlatformType();// (e.g. Mobile Phone, Desktop, Tablet, Console, TV Device)
    let OSName = BrowserService.getOSName();// (e.g. Android, iOS, Win7, Win8, Win10)
    let OSVersion = BrowserService.getOSVersion();// (e.g. 4.2, 10 depending on what the platform is)
    
    let currentCourseId : number = LocalStorageService.getItem(COURSE_TABS.COURSE_OBJ)?.id;
    let currentClassId : number = LocalStorageService.getItem(COURSE_TABS.COURSE_OBJ)?.classId;
    let currentSectionId : number = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_SECTION_STATE)?.[currentCourseId];
    let currentMaterialId : number = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_MATERIAL_STATE)?.[currentCourseId];
    let currentProjectId : number = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_PROJECT_STATE)?.[currentCourseId];
    let connectEventParameter = { deviceInfo: `${platformType}|${OSName}|${OSVersion}|${browserName}|${browserVersion}`, currentCourseId, currentClassId, currentSectionId, currentMaterialId, currentProjectId};
    
    this.emitScreenMirroringEvent(element, connectEventParameter, 'connect');
  }

  public screenModeSelection(element: Vue, screenModeSelected: string, notifyToBackend: boolean = true) {
    if(screenModeSelected === 'book') {
      this.createListenerBookMode(element, notifyToBackend);
    } else if (screenModeSelected === 'paper') {
      this.createListenerPaperMode(element, notifyToBackend);
    } else {
      this.createListenerNormalMode(element, notifyToBackend);
    }
    element.$store.commit(_APP_STORE.MUTATIONS.SET_USER_MODE, screenModeSelected);
    LocalStorageService.setItem(APP_STORAGE_KEYS.STUDENT_MODE, screenModeSelected.toUpperCase());
  }

  public createListener(element: Vue, mode: string, notifyDeviceRegistration: boolean) {
    element.$store.commit(SCREEN_MIRRORING_STORE.MUTATIONS.SET_MODE, mode);
    if (notifyDeviceRegistration) {
      this.registerDevice(element);
    }
  }

  public screenModeValidationBackEndRequest(element: Vue) {
    this.emitScreenMirroringEvent(element, {}, 'list');
  }

  public async handleScreenModeValidation(element: Vue, message: WebSocketMessageResponse) {
    let screenModeSession = JSON.parse(message.content);
    if (screenModeSession.session.length == 0) {
      this.screenModeSelection(element, 'normal');
    } else if (screenModeSession.session.length === 1) {
      var deviceInfo = screenModeSession.session[0].deviceInfo;
      var deviceName = this.getDeviceName(element, deviceInfo);
      element.$EdModal.createConfirmationModal({
        title: element.$t('screenMirroring.screenModeValidationTitle'),
        body: element.$t('screenMirroring.screenModeValidationMessage'),
        actionText: element.$t('screenMirroring.accept'),
        action: (res) => {
          let screenModeSelected: string = res.optionRadioValue;
          this.screenModeSelection(element, screenModeSelected);
          element.$nextTick(() => {
            this.emitScreenMirroringEvent(element, 
              {
                target: SCREEN_MIRRORING_EVENTS.GO_TO_PAGE,
                name: PRIVATE_ROUTES.PORTAL.NAME,
              }
            );
          });
        },
        secondActionText: element.$t('screenMirroring.ignore'),
        secondAction: () => {
          this.screenModeSelection(element, 'normal');
        },
        hasOptionRadio: true,
        radios: [{
          name: 'normal',
          icon: 'study-book',
          label: element.$t('screenMirroring.normalModeLabel'),
          disclamer: ''
        }, {
          name: 'paper',
          icon: 'pencil-paper',
          label: element.$t('screenMirroring.paperModeLabel'),
          disclamer: element.$t('screenMirroring.screenModeValidationPaperDisclaimer', { deviceName }),
        }, {
          name: 'book',
          icon: 'book-solid',
          label: element.$t('screenMirroring.bookModeLabel'),
          disclamer: element.$t('screenMirroring.screenModeValidationBookDisclaimer', { deviceName }),
        }]
      });
    } else {
      this.screenModeSelection(element, 'normal', false);
    }
  }

  private getDeviceName(element: Vue, deviceInfo: string): string {
    let deviceInfoParts = deviceInfo.split('|');
    if (deviceInfoParts.length === 1) {
      return deviceInfo;
    }

    let [deviceType, platform, platformVersion, browser] = deviceInfoParts;
    let locale = Tools.getCookie(KEYS.LANGUAGE_COOKIE);
    if (locale === 'en') {
      return `${deviceType} ${platform} ${platformVersion} ${browser}`;
    } else {
      let deviceTypeTranslated = element.$t(`screenMirroring.device.${deviceType}`);
      return `${deviceTypeTranslated} ${platform} ${platformVersion} ${browser}`;
    }
  }

  public emitScreenMirroringEvent(element: Vue, event_args: any, message_type: string = 'click') {
    let mode : string = element.$store.state.ScreenMirroringStore.mode || 'normal';
    if ( 'normal' === mode && message_type === 'click') {
      return;
    }
    event_args.clientId = element.$store.state.ScreenMirroringStore.clientId;
    event_args.mode = mode;
    let userId = element.$store.state.AuthStore.user.userId;
    let url = `/app/interactive/screenMirroring/${message_type}`;
    let message: WebSocketMessageRequest = EdPresentationModeFactory.createWebSocketMessageRequest(message_type, userId, false, JSON.stringify(event_args));
    element.$EdSocket.emit(url, message);
  }

  public isPaperModeActive(element: Vue) {
    return element.$store.state.ScreenMirroringStore.mode == 'paper';
  }

  public isBookModeActive(element: Vue) {
    return element.$store.state.ScreenMirroringStore.mode == 'book';
  }

}

export const ScreenMirroringService = new ScreenMirroringServiceClass();
